import type { AuthLegalLink } from "@huub-web/auth";
import type { ThemeName } from "@huub-web/ui";

/**
 * Legal links and statutory notice for one deployment.
 */
export type LegalContent = {
  /**
   * Legal documents to link to (e.g. privacy statement, comparison card)
   */
  links: AuthLegalLink[];
  /**
   * i18n key of the statutory identification of the licence holder, if any
   */
  notice?: string;
};

/**
 * Per-deployment legal content. The PDFs under `/assets/documents/` are
 * copied from `resources/public/assets` at build time and keep a
 * version-free filename, so these URLs stay stable across new versions.
 */
const LEGAL_CONTENT_BY_THEME: Partial<Record<ThemeName, LegalContent>> = {
  light: {
    links: [
      {
        labelKey: "authScreens.legalLinks.comparisonCard",
        url: "/assets/documents/Huub_Hypotheken-Vergelijkingskaart.pdf",
      },
      {
        labelKey: "authScreens.legalLinks.privacyStatement",
        url: "/assets/documents/Huub_Hypotheken-Privacy_Statement.pdf",
      },
    ],
    notice: "authScreens.legalLinks.huubHypothekenNotice",
  },
  verenigingEigenHuis: {
    links: [
      {
        labelKey: "authScreens.legalLinks.privacyStatement",
        url: "https://www.eigenhuis.nl/privacystatement",
      },
      {
        labelKey: "authScreens.legalLinks.termsAndConditions",
        url: "https://www.eigenhuis.nl/algemene-voorwaarden",
      },
    ],
  },
};

/**
 * Resolves the legal content for a deployment theme. Themes without an
 * entry (other whitelabels) get no links and no notice.
 */
export const getLegalContent = ({
  theme,
}: {
  theme: ThemeName;
}): LegalContent => LEGAL_CONTENT_BY_THEME[theme] ?? { links: [] };

/**
 * Legal content of this deployment. Keyed off `VITE_THEME`, not the
 * user-switchable theme setting, so a whitelabel build never shows Huub's.
 */
export const legalContent = getLegalContent({
  theme: (import.meta.env.VITE_THEME as ThemeName) || "light",
});
